import {
  createDiscoverySessionMutationOptions,
  currentDiscoverySessionQueryOptions,
  discoveryQueryKeys,
  type DiscoverySession,
} from '@/entities/discovery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { atom, useAtom } from 'jotai';
import { useCallback, type SetStateAction } from 'react';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';

const viewStateAtom = atom<'initializing' | 'idle' | 'chat'>('initializing');
const promptAtom = atom('');
const isSearchingAtom = atom<boolean>(false);

export const useDiscoveryStore = () => {
  const { workspaceId } = useWorkspaceId();
  const queryClient = useQueryClient();
  const [viewState, setViewState] = useAtom(viewStateAtom);
  const [prompt, setPrompt] = useAtom(promptAtom);

  const [isSearching, setIsSearching] = useAtom(isSearchingAtom);

  const sessionQuery = useQuery(currentDiscoverySessionQueryOptions(workspaceId));
  const session = sessionQuery.data ?? null;
  const createSessionMutation = useMutation(
    createDiscoverySessionMutationOptions(queryClient, workspaceId),
  );
  const setSession = useCallback(
    (nextSession: SetStateAction<DiscoverySession | null>) => {
      queryClient.setQueryData<DiscoverySession | null>(
        discoveryQueryKeys.currentSession(workspaceId),
        (currentSession) =>
          typeof nextSession === 'function' ? nextSession(currentSession ?? null) : nextSession,
      );
    },
    [queryClient, workspaceId],
  );

  const resetSession = async () => {
    setViewState('initializing');
    if (session) {
      queryClient.removeQueries({
        queryKey: discoveryQueryKeys.messagesRoot(workspaceId, session.session_id),
      });
    }
    setPrompt('');
    setIsSearching(false);
    await createSessionMutation.mutateAsync();
    setViewState('idle');
  };

  return {
    session,
    sessionQuery,
    createSessionMutation,
    setSession,
    viewState,
    setViewState,
    prompt,
    setPrompt,
    isSearching,
    setIsSearching,
    resetSession,
  };
};
